import { CookieService } from 'ngx-cookie-service';
import { Subject } from 'rxjs';
import { delay, filter, takeUntil } from 'rxjs/operators';

import {
	Component,
	HostListener,
	OnDestroy,
	OnInit,
	VERSION,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { TranslateService } from '@ngx-translate/core';

import { environment } from './core/environments/environment';
import { LoadingService } from './core/services/loading/loading.service';
import { SecureStorageService } from './core/services/secure-storage/secure-storage.service';
import { UserPermissionsService } from './core/services/user-permissions/user-permissions.service';
import { Utils } from './shared/helper/Utils';

/** Class for the app component that is bootstrapped to run the application
 */
@Component({
    selector: 'body',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
	/** string used to hold the url for the skipToMain link */
	skipToMain: string;
	/** the Angular version */
	version = VERSION.full;
	/** whether we are production or not */
	envProd = environment.production;
	/** constructor for setting up DI in this component */
	displayDialog: boolean;
	fullMessage: boolean = true;
	year = Utils.getYear();

	loading: boolean = false;

	/** Subject used to signal when component is in ngOnDestroy */
	private readonly _destroying$ = new Subject<void>();
	private tokenConfigLogged = false;

	@HostListener('window:resize', ['$event']) onresize(event?: {
		target: { innerWidth: number };
	}) {
		if (event.target.innerWidth > 767) {
			this.fullMessage = true;
		} else {
			this.fullMessage = false;
		}
	}

	constructor(
		private readonly router: Router,
		private cookieService: CookieService,
		private translateService: TranslateService,
		private _loading: LoadingService,
		private userPermissionsService: UserPermissionsService,
		private authService: MsalService,
		private msalBroadcastService: MsalBroadcastService,
		private secureStorage: SecureStorageService,
	) {
		this.translateService.setDefaultLang('pt');

		if (this.cookieService.get('language')) {
			this.translateService.use(this.cookieService.get('language'));
		} else {
			this.translateService.use('pt');
			this.cookieService.set('language', 'pt');
		}
	}

	/** this class requires this method to implement the OnInit interface */
	ngOnInit() {
		this.listenToLoading();

		this.msalBroadcastService.inProgress$
			.pipe(
				filter(
					(status: InteractionStatus) =>
						status === InteractionStatus.None,
				),
				takeUntil(this._destroying$),
			)
			.subscribe(() => {
				this.checkAndSetActiveAccount();
				this.setUserInfoFromMsal(() => {
					// Carrega as permissões somente depois que o token foi salvo
					this.loadUserPermissions();
				});
			});

		this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe((event: NavigationEnd) => {
				this.setSkipLinkUrl(event.urlAfterRedirects);
			});

		
	}

	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

	/**
	 * Checks if there is an active account set. If not, sets the first signed-in account as the active account.
	 * Note: Basic usage demonstrated. Your app may require more complicated account selection logic.
	 */
	checkAndSetActiveAccount() {
		const activeAccount = this.authService.instance.getActiveAccount();
		if (
			!activeAccount &&
			this.authService.instance.getAllAccounts().length > 0
		) {
			const accounts = this.authService.instance.getAllAccounts();
			this.authService.instance.setActiveAccount(accounts[0]);
		}
	}

	/**
	 * Extrai informações do usuário autenticado via MSAL e armazena no sessionStorage
	 */
	setUserInfoFromMsal(onTokenReady?: () => void) {
		const activeAccount = this.authService.instance.getActiveAccount();
		if (activeAccount) {
			const username =
				activeAccount.username?.split('@')[0] || activeAccount.name;
			const name = activeAccount.name;

			this.secureStorage.setItem('username', username);
			this.secureStorage.setItem('userId', username);
			this.secureStorage.setItem('userFullName', name);
			this.secureStorage.setItem('country', environment.country);

			this.authService
				.acquireTokenSilent({
					scopes: environment.apiConfig.scopes,
					account: activeAccount,
				})
				.subscribe({
					next: (result) => {
						this.logTokenRequestConfig();
						const token = result.accessToken || result.idToken;
						this.secureStorage.setItem('token', token);
						console.log('Token JWT armazenado com sucesso.');
						onTokenReady?.();
						this.reloadAfterTokenReady();
					},
					error: (err) => {
						console.error('Erro ao adquirir token silenciosamente:', err);
						// fallback: usa o idToken da conta ativa se disponível
						const idToken = (activeAccount as any).idToken as string;
						if (idToken) {
							this.secureStorage.setItem('token', idToken);
							console.warn('Usando idToken como fallback.');
							onTokenReady?.();
							this.reloadAfterTokenReady();
						}
					},
				});
		}
	}

	private reloadAfterTokenReady(): void {
		const RELOAD_FLAG = 'postLoginReloadDone';
		if (sessionStorage.getItem(RELOAD_FLAG)) {
			return;
		}
		sessionStorage.setItem(RELOAD_FLAG, 'true');
		window.location.reload();
	}

	/**
	 * Registra no console os parâmetros usados para aquisição do token.
	 * Observação: em apps Angular/MSAL, `client_secret` não é usado no front-end.
	 */
	private logTokenRequestConfig(): void {
		if (this.tokenConfigLogged) {
			return;
		}

		this.tokenConfigLogged = true;
		console.log('[Auth] Configuração usada para aquisição do token:', {
			authority: environment.msalConfig.authority,
			client_id: environment.msalConfig.clientId,
			client_secret: 'NÃO USADO NO FRONT-END (MSAL público/PKCE)',
			scopes: environment.apiConfig.scopes,
			backend_url: environment.apiConfig.uri || environment.financialUrl,
		});
	}

	/**
	 * Carrega as permissões do usuário após a autenticação
	 */
	loadUserPermissions() {
		const token = this.secureStorage.getItem('token');
		if (!token) {
			console.warn('Token indisponível; permissões não serão carregadas agora.');
			return;
		}

		this.userPermissionsService.getPermissions().subscribe(
			(result) => {
				let permissions = result;
				permissions = Array.from(new Set(permissions));
				this.secureStorage.setItem(
					'permissions',
					JSON.stringify(permissions),
				);
				console.log('Permissões do usuário carregadas:', permissions);
			},
			(error) => {
				console.error('Erro ao carregar permissões:', error);
			},
		);
	}

	/**
	 * Listen to the loadingSub property in the LoadingService class. This drives the
	 * display of the loading spinner.
	 */
	listenToLoading(): void {
		this._loading.loadingSub
			.pipe(delay(0))
			.subscribe((loading) => {
				this.loading = loading;
			});
	}

	/**
	 * setSkipLinkUrl takes in a url string and processes whether
	 * the skipToMain link should be updated to use the new value
	 * @param currentUrl the new url to refer to
	 */
	private setSkipLinkUrl(currentUrl: string) {
		if (!currentUrl.endsWith('#app-content')) {
			this.skipToMain = `${currentUrl}#app-content`;
		}
	}

	showDialog() {
		this.displayDialog = true;
	}
}
